import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type {
  AccountBranch,
  AccountContact,
  ContactStatus,
  CustomerAccountDetail,
} from '@central/contracts';
import { ArrowLeft, Building2, FileText, KeyRound, Package, Pencil, Plus, UserRound } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonRegion, SkeletonText } from '@/components/Skeleton';
import {
  chavesDeContratos,
  listarContratos,
  obterServicosDoCliente,
  type AccountCoveredService,
  type ContractSummary,
} from '@/features/contratos/contratos.api';
import {
  enderecoDoContrato,
  situacaoDeContrato,
  textoDeVencimento,
  tipoDeContrato,
  tomDeVencimento,
} from '@/features/contratos/navegacao';
import { DialogoDeContrato, Etiqueta } from '@/features/contratos/componentes';
import { formatarDataHora } from '@/lib/format';
import {
  BotaoDeIcone,
  Cabecalho,
  Campo,
  Celula,
  CLASSE_DE_CAMPO,
  Linha,
  LinhaDeCabecalho,
} from '@/features/admin/componentes';
import {
  alterarContato,
  chavesDeClientes,
  criarContato,
  definirSenhaDeContato,
  listarContatos,
  obterCliente,
} from '@/features/clientes/clientes.api';
import {
  Contagem,
  DialogoDeEdicaoDeCliente,
  EtiquetaDeSituacao,
  useEscritaDeCliente,
} from '@/features/clientes/componentes';
import {
  CAMINHO_LISTA,
  MODOS_DE_AUTOCADASTRO,
  SITUACOES_DE_CONTATO,
  situacaoDeConta,
  situacaoDeContato,
} from '@/features/clientes/navegacao';
import { Modal } from '@/features/tickets/Modal';

/**
 * A ficha do cliente: os dados comerciais, as filiais e os contatos.
 *
 * Uma tela só, e não três abas, porque as três respondem à mesma pergunta —
 * "quem é este cliente e quem fala por ele" — e porque a lista de filiais tem
 * uma linha na carteira real de hoje (§e.7: o objeto `branch` do Topdesk não
 * tem campo de pai, e toda conta nasce plana). Uma aba para uma linha é uma
 * aba que ninguém abre.
 */
export function ClienteDetalhePage({ clienteId }: { clienteId: string }) {
  const cliente = useQuery({
    queryKey: chavesDeClientes.ficha(clienteId),
    queryFn: () => obterCliente(clienteId),
  });

  if (cliente.isError) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <VoltarParaLista />
        <ErrorState
          error={cliente.error}
          title="Não foi possível abrir este cliente"
          onRetry={() => void cliente.refetch()}
        />
      </div>
    );
  }

  if (cliente.isPending) {
    return (
      <SkeletonRegion label="Carregando o cliente" className="mx-auto max-w-5xl space-y-4">
        <SkeletonText lines={6} />
      </SkeletonRegion>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 animate-fade-in">
      <VoltarParaLista />
      <CabecalhoDoCliente cliente={cliente.data} />
      <ServicosCobertosDoConta clienteId={clienteId} />
      <Filiais filiais={cliente.data.branches} />
      <Contatos clienteId={clienteId} nomeDoCliente={cliente.data.name} />
      <ContratosDoCliente clienteId={clienteId} />
    </div>
  );
}

function VoltarParaLista() {
  return (
    <Link
      to={CAMINHO_LISTA}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:shadow-glow focus-visible:outline-none"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      Clientes
    </Link>
  );
}

// ── cabeçalho e dados comerciais ───────────────────────────────────────────

function CabecalhoDoCliente({ cliente }: { cliente: CustomerAccountDetail }) {
  const [editando, setEditando] = useState(false);
  const situacao = situacaoDeConta(cliente.status);

  return (
    <>
      <Card>
        <CardHeader
          title={
            <span className="flex flex-wrap items-center gap-2">
              {cliente.name}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-normal">
                {cliente.code}
              </code>
              <EtiquetaDeSituacao
                rotulo={situacao.rotulo}
                tom={situacao.tom}
                titulo={situacao.efeito}
              />
            </span>
          }
          description={cliente.legalName}
          action={
            <Button
              size="sm"
              variant="secondary"
              // `shrink-0`: o `CardHeader` é um flex, e sem isto o botão cede
              // largura ao texto ao lado e quebra o rótulo em duas linhas.
              className="shrink-0"
              onClick={() => setEditando(true)}
              icon={<Pencil className="h-4 w-4" aria-hidden />}
            >
              Editar
            </Button>
          }
        />
        <CardBody className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          <Dado rotulo="CNPJ" valor={cliente.taxId} mono />
          <Dado rotulo="Domínio de e-mail" valor={cliente.domain} mono />
          <Dado
            rotulo="Portal"
            valor={cliente.portalEnabled ? 'Habilitado' : 'Desabilitado'}
          />
          <Dado
            rotulo="Autocadastro no portal"
            valor={
              MODOS_DE_AUTOCADASTRO.find(([valor]) => valor === cliente.portalSelfSignup)?.[1] ??
              cliente.portalSelfSignup
            }
          />
          <Dado
            rotulo="Domínios aceitos"
            valor={cliente.portalEmailDomains.join(', ') || null}
          />
          <Dado rotulo="Cadastrado em" valor={formatarDataHora(cliente.createdAt)} />
          {cliente.notes ? (
            <div className="sm:col-span-2 lg:col-span-3">
              <Dado rotulo="Observações" valor={cliente.notes} />
            </div>
          ) : null}
        </CardBody>
      </Card>

      <DialogoDeEdicaoDeCliente
        aberto={editando}
        cliente={cliente}
        aoFechar={() => setEditando(false)}
      />
    </>
  );
}

function Dado({
  rotulo,
  valor,
  mono = false,
}: {
  rotulo: string;
  valor: string | null;
  mono?: boolean;
}) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{rotulo}</dt>
      <dd className={mono ? 'font-mono text-sm' : 'text-sm'}>{valor ?? '—'}</dd>
    </div>
  );
}

// ── serviços cobertos ──────────────────────────────────────────────────────

/**
 * Todos os serviços que o cliente tem cobertos por algum contrato ativo.
 *
 * É a resposta direta à pergunta "o que está contratado?" — não exige
 * navegar para Contratos, abrir cada um e ver os termos. O card mostra
 * serviço → por qual contrato → com qual política de SLA.
 */
function ServicosCobertosDoConta({ clienteId }: { clienteId: string }) {
  const consulta = useQuery({
    queryKey: chavesDeContratos.servicosDaConta(clienteId),
    queryFn: () => obterServicosDoCliente(clienteId),
  });

  const servicos = consulta.data?.items ?? [];

  return (
    <Card>
      <CardHeader
        title="Serviços cobertos"
        description="O que está contratado e ativo agora — por serviço, indicando o contrato e a política de SLA."
      />
      <CardBody className="p-0">
        {consulta.isPending ? (
          <SkeletonRegion label="Carregando serviços cobertos" className="px-5 py-4">
            <SkeletonText lines={3} />
          </SkeletonRegion>
        ) : consulta.isError ? (
          <div className="px-5 py-6">
            <ErrorState
              error={consulta.error}
              title="Não foi possível carregar os serviços"
              onRetry={() => void consulta.refetch()}
            />
          </div>
        ) : servicos.length === 0 ? (
          <div className="px-5 py-10">
            <EmptyState
              icon={<Package className="h-7 w-7" aria-hidden />}
              title="Nenhum serviço coberto no momento"
              description="Nenhum contrato ativo tem serviços cadastrados. Adicione um termo ativo com serviços em Contratos."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <LinhaDeCabecalho>
                <Cabecalho>Serviço</Cabecalho>
                <Cabecalho>Contrato</Cabecalho>
                <Cabecalho>Política de SLA</Cabecalho>
                <Cabecalho>Portal</Cabecalho>
              </LinhaDeCabecalho>
              <tbody>
                {servicos.map((servico) => (
                  <LinhaDeServico key={servico.serviceId} servico={servico} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function LinhaDeServico({ servico }: { servico: AccountCoveredService }) {
  return (
    <>
      {servico.contracts.map((contrato) => (
        <Linha key={`${servico.serviceId}-${contrato.contractId}`} inativo={false}>
          <Celula className="font-medium">{servico.serviceName}</Celula>
          <Celula className="text-xs text-muted-foreground">
            <Link
              {...enderecoDoContrato(contrato.contractId)}
              className="hover:text-primary focus-visible:shadow-glow focus-visible:outline-none"
            >
              {contrato.contractName}
            </Link>
            <span className="ml-1 font-mono text-xs opacity-60">{contrato.contractNumber}</span>
          </Celula>
          <Celula className="text-xs text-muted-foreground">
            {contrato.slaPolicyName ?? <span className="italic opacity-60">sem política</span>}
          </Celula>
          <Celula>
            <EtiquetaDeSituacao
              rotulo={contrato.visibleInPortal ? 'Visível' : 'Oculto'}
              tom={contrato.visibleInPortal ? 'sucesso' : 'neutro'}
              titulo={
                contrato.visibleInPortal
                  ? 'O cliente vê este serviço no portal'
                  : 'Serviço interno — não aparece para o cliente'
              }
            />
          </Celula>
        </Linha>
      ))}
    </>
  );
}

// ── contratos do cliente ───────────────────────────────────────────────────

/**
 * A carteira de contratos deste cliente — resumo, não gestão.
 *
 * A gestão está em `/contratos`. Aqui é o painel da ficha: ver que contratos
 * existem e qual está vigente, com um link para cada um. O card não aparece
 * vazio quando a lista está carregando — o esqueleto ocupa o lugar.
 */
function ContratosDoCliente({ clienteId }: { clienteId: string }) {
  const [criando, setCriando] = useState(false);

  const consulta = useQuery({
    queryKey: chavesDeContratos.lista({ accountId: clienteId }),
    queryFn: () => listarContratos({ accountId: clienteId }),
  });

  const contratos = consulta.data?.items ?? [];

  return (
    <>
    <Card>
      <CardHeader
        title="Contratos"
        description="Vigências, franquias e cobertura combinada com este cliente. A gestão completa fica em Contratos."
        action={
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setCriando(true)}
              icon={<Plus className="h-4 w-4" aria-hidden />}
            >
              Novo contrato
            </Button>
            <Link
              to="/contratos"
              className="inline-flex h-8 items-center rounded-md border border-border bg-secondary px-3 text-sm font-medium transition-colors hover:bg-accent focus-visible:shadow-glow focus-visible:outline-none"
            >
              Ver carteira
            </Link>
          </div>
        }
      />
      <CardBody className="p-0">
        {consulta.isPending ? (
          <SkeletonRegion label="Carregando contratos" className="px-5 py-4">
            <SkeletonText lines={3} />
          </SkeletonRegion>
        ) : contratos.length === 0 ? (
          <div className="px-5 py-10">
            <EmptyState
              icon={<FileText className="h-7 w-7" aria-hidden />}
              title="Nenhum contrato com este cliente"
              description="Sem contrato, os chamados deste cliente nascem marcados como 'sem acordo'."
            />
          </div>

        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <LinhaDeCabecalho>
                <Cabecalho>Contrato</Cabecalho>
                <Cabecalho>Tipo</Cabecalho>
                <Cabecalho>Vigência</Cabecalho>
                <Cabecalho>Situação</Cabecalho>
              </LinhaDeCabecalho>
              <tbody>
                {contratos.map((contrato) => (
                  <LinhaDeContrato key={contrato.id} contrato={contrato} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>

    <DialogoDeContrato
      aberto={criando}
      aoFechar={() => setCriando(false)}
      accountIdInicial={clienteId}
    />
    </>
  );
}

function LinhaDeContrato({ contrato }: { contrato: ContractSummary }) {
  const situacao = situacaoDeContrato(contrato.status);
  const tom = tomDeVencimento(contrato.diasParaVencer);
  const inativo = contrato.status === 'TERMINATED' || contrato.status === 'EXPIRED';

  return (
    <Linha inativo={inativo}>
      <Celula>
        <Link
          {...enderecoDoContrato(contrato.id)}
          className="font-medium text-foreground hover:text-primary focus-visible:shadow-glow focus-visible:outline-none"
        >
          {contrato.name}
        </Link>
        <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
          {contrato.number}
          {contrato.externalNumber ? ` · ${contrato.externalNumber}` : ''}
        </span>
      </Celula>
      <Celula className="text-xs text-muted-foreground">
        {tipoDeContrato(contrato.type).rotulo}
      </Celula>
      <Celula className="text-xs tabular-nums text-muted-foreground">
        {contrato.vigenteDe
          ? `${contrato.vigenteDe.slice(0, 10)} → ${contrato.vigenteAte ? contrato.vigenteAte.slice(0, 10) : '∞'}`
          : '—'}
        {contrato.diasParaVencer !== null ? (
          <span className={tom === 'perigo' ? 'ml-2 text-destructive' : tom === 'alerta' ? 'ml-2 text-warning' : ''}>
            ({textoDeVencimento(contrato.diasParaVencer, contrato.vigenteDe !== null)})
          </span>
        ) : null}
      </Celula>
      <Celula>
        <Etiqueta rotulo={situacao.rotulo} tom={situacao.tom} titulo={situacao.origem} />
      </Celula>
    </Linha>
  );
}

// ── filiais ────────────────────────────────────────────────────────────────

/**
 * As organizações da conta, planas e com a matriz marcada.
 *
 * A árvore existe no modelo — `Organization.accountId` aponta para a raiz —,
 * mas a carteira real é rasa: o objeto `branch` do Topdesk não tem campo de
 * pai, então toda conta importada nasce com uma organização só (§e.7). A tela
 * mostra a estrutura que houver, sem prometer uma que não existe.
 *
 * Cadastrar filial é da tela de organizações, e por isso não há botão aqui:
 * duas portas para a mesma tabela dariam dois formulários que divergem.
 */
function Filiais({ filiais }: { filiais: AccountBranch[] }) {
  return (
    <Card>
      <CardHeader
        title="Filiais"
        description="As organizações que pertencem a esta conta. A matriz é a própria conta — é ela que o isolamento por cliente compara."
      />
      <CardBody className="p-0">
        {filiais.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState
              icon={<Building2 className="h-6 w-6" aria-hidden />}
              title="Nenhuma organização nesta conta"
              description="Isso não deveria acontecer: a matriz é a própria conta. Verifique o cadastro na tela de organizações."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <LinhaDeCabecalho>
                <Cabecalho>Organização</Cabecalho>
                <Cabecalho>CNPJ</Cabecalho>
                <Cabecalho>Chamados</Cabecalho>
                <Cabecalho>Estado</Cabecalho>
              </LinhaDeCabecalho>
              <tbody>
                {filiais.map((filial) => (
                  <Linha key={filial.id} inativo={!filial.active}>
                    <Celula>
                      {/* O recuo desenha a profundidade sem exigir uma árvore
                          de verdade — hoje ela é sempre 0 ou 1. */}
                      <span
                        className="font-medium"
                        style={{ paddingLeft: `${filial.depth * 1.25}rem` }}
                      >
                        {filial.name}
                      </span>
                      {filial.isRoot ? (
                        <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                          Matriz
                        </span>
                      ) : null}
                    </Celula>
                    <Celula className="font-mono text-xs text-muted-foreground">
                      {filial.taxId ?? '—'}
                    </Celula>
                    <Celula>
                      <Contagem valor={filial.tickets} singular="chamado" plural="chamados" />
                    </Celula>
                    <Celula>
                      <EtiquetaDeSituacao
                        rotulo={filial.active ? 'Ativa' : 'Inativa'}
                        tom={filial.active ? 'sucesso' : 'neutro'}
                      />
                    </Celula>
                  </Linha>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ── contatos ───────────────────────────────────────────────────────────────

/**
 * Os usuários do cliente — §b.7.
 *
 * Desligados **ficam na lista**, marcados. É a informação que a tela existe
 * para dar: "esta pessoa não entra mais" é diferente de "esta pessoa nunca
 * existiu", e o histórico de chamados continua sendo dela.
 */
function Contatos({ clienteId, nomeDoCliente }: { clienteId: string; nomeDoCliente: string }) {
  const consulta = useQuery({
    queryKey: chavesDeClientes.contatos(clienteId),
    queryFn: () => listarContatos(clienteId),
  });

  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<AccountContact | null>(null);
  const [definindoSenha, setDefinindoSenha] = useState<AccountContact | null>(null);

  const contatos = consulta.data?.items ?? [];

  return (
    <>
      <Card>
        <CardHeader
          title="Contatos"
          description={`As pessoas de ${nomeDoCliente} que abrem e acompanham chamados. Desligar tira do portal e derruba as sessões — o cadastro fica, porque os chamados dele continuam sendo dele.`}
          action={
            <Button
              size="sm"
              className="shrink-0"
              onClick={() => setCriando(true)}
              icon={<Plus className="h-4 w-4" aria-hidden />}
            >
              Novo contato
            </Button>
          }
        />
        <CardBody className="p-0">
          {consulta.isError ? (
            <div className="px-5 py-6">
              <ErrorState
                error={consulta.error}
                title="Não foi possível carregar os contatos"
                onRetry={() => void consulta.refetch()}
              />
            </div>
          ) : consulta.isPending ? (
            <SkeletonRegion label="Carregando os contatos" className="space-y-3 px-5 py-5">
              <SkeletonText lines={3} />
            </SkeletonRegion>
          ) : contatos.length === 0 ? (
            <div className="px-5 py-8">
              <EmptyState
                icon={<UserRound className="h-6 w-6" aria-hidden />}
                title="Nenhum contato cadastrado"
                description="Sem contato, ninguém deste cliente entra no portal e todo chamado precisa ser aberto pela Aptum em nome dele."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <LinhaDeCabecalho>
                  <Cabecalho>Contato</Cabecalho>
                  <Cabecalho>Cargo</Cabecalho>
                  <Cabecalho>Telefone</Cabecalho>
                  <Cabecalho>Chamados</Cabecalho>
                  <Cabecalho>Último acesso</Cabecalho>
                  <Cabecalho>Situação</Cabecalho>
                  <Cabecalho className="text-right">Ações</Cabecalho>
                </LinhaDeCabecalho>
                <tbody>
                  {contatos.map((contato) => {
                    const situacao = situacaoDeContato(contato.status);
                    return (
                      <Linha key={contato.id} inativo={!contato.active}>
                        <Celula>
                          <span className="font-medium">{contato.name}</span>
                          <span className="mt-0.5 block text-xs text-muted-foreground">
                            {contato.email}
                          </span>
                        </Celula>
                        <Celula className="text-xs text-muted-foreground">
                          {contato.jobTitle ?? '—'}
                        </Celula>
                        <Celula className="text-xs text-muted-foreground">
                          {contato.phone ?? '—'}
                        </Celula>
                        <Celula>
                          <Contagem
                            valor={contato.tickets}
                            singular="chamado"
                            plural="chamados"
                          />
                        </Celula>
                        <Celula className="text-xs text-muted-foreground">
                          {contato.lastLoginAt ? formatarDataHora(contato.lastLoginAt) : 'nunca'}
                        </Celula>
                        <Celula>
                          <EtiquetaDeSituacao
                            rotulo={situacao.rotulo}
                            tom={situacao.tom}
                            titulo={situacao.efeito}
                          />
                          {contato.activeSessions > 0 ? (
                            <span className="mt-0.5 block text-xs text-muted-foreground tabular-nums">
                              {contato.activeSessions}{' '}
                              {contato.activeSessions === 1 ? 'sessão viva' : 'sessões vivas'}
                            </span>
                          ) : null}
                        </Celula>
                        <Celula>
                          <div className="flex justify-end gap-1">
                            <BotaoDeIcone
                              rotulo={`Definir senha de ${contato.name}`}
                              aoClicar={() => setDefinindoSenha(contato)}
                              icone={<KeyRound className="h-4 w-4" aria-hidden />}
                            />
                            <BotaoDeIcone
                              rotulo={`Editar ${contato.name}`}
                              aoClicar={() => setEditando(contato)}
                              icone={<Pencil className="h-4 w-4" aria-hidden />}
                            />
                          </div>
                        </Celula>
                      </Linha>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <DialogoDeContato
        aberto={criando || editando !== null}
        clienteId={clienteId}
        contato={editando}
        aoFechar={() => {
          setCriando(false);
          setEditando(null);
        }}
      />

      {definindoSenha && (
        <DefinirSenhaModal
          accountId={clienteId}
          contato={definindoSenha}
          aberto={true}
          aoFechar={() => setDefinindoSenha(null)}
        />
      )}
    </>
  );
}

function DialogoDeContato({
  aberto,
  clienteId,
  contato,
  aoFechar,
}: {
  aberto: boolean;
  clienteId: string;
  contato: AccountContact | null;
  aoFechar: () => void;
}) {
  const editando = contato !== null;

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [cargo, setCargo] = useState('');
  const [telefone, setTelefone] = useState('');
  const [status, setStatus] = useState<ContactStatus>('INVITED');
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [semeado, setSemeado] = useState<string | null>(null);

  const identidade = contato ? `${contato.id}:${contato.version}` : aberto ? 'novo' : null;
  if (aberto && semeado !== identidade) {
    setSemeado(identidade);
    setNome(contato?.name ?? '');
    setEmail(contato?.email ?? '');
    setCargo(contato?.jobTitle ?? '');
    setTelefone(contato?.phone ?? '');
    setStatus(contato?.status ?? 'INVITED');
    setSenha('');
    setConfirmarSenha('');
  }
  if (!aberto && semeado !== null) setSemeado(null);

  const escrita = useEscritaDeCliente(async () => {
    if (contato) {
      return alterarContato(clienteId, contato.id, {
        name: nome.trim(),
        jobTitle: cargo.trim(),
        phone: telefone.trim(),
        status,
        version: contato.version,
      });
    }
    return criarContato(clienteId, {
      email: email.trim().toLowerCase(),
      name: nome.trim(),
      ...(cargo.trim() ? { jobTitle: cargo.trim() } : {}),
      ...(telefone.trim() ? { phone: telefone.trim() } : {}),
      status: status === 'ACTIVE' ? 'ACTIVE' : 'INVITED',
      ...(senha.trim() ? { initialPassword: senha.trim() } : {}),
    });
  }, aoFechar);

  const situacao = situacaoDeContato(status);
  const derrubaSessoes =
    editando && contato.activeSessions > 0 && status !== 'ACTIVE' && contato.status === 'ACTIVE';

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    escrita.mutacao.mutate();
  }

  /** Na criação só existem os dois estados iniciais — ver o DTO do servidor. */
  const opcoes = editando
    ? SITUACOES_DE_CONTATO
    : SITUACOES_DE_CONTATO.filter((s) => s.valor === 'INVITED' || s.valor === 'ACTIVE');

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={editando ? `Editar “${contato.name}”` : 'Novo contato'}
      descricao={
        editando
          ? 'O e-mail não muda: ele é a identidade de login e a chave por que a resposta do cliente cai no chamado certo. Quem trocou de endereço é convidado de novo.'
          : 'O contato nasce convidado — cadastrado, sem senha, e já podendo aparecer como solicitante de chamado.'
      }
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        <Campo id="ct-nome" rotulo="Nome">
          <input
            id="ct-nome"
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
            required
            minLength={2}
            maxLength={200}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo id="ct-email" rotulo="E-mail">
          <input
            id="ct-email"
            type="email"
            value={email}
            onChange={(evento) => setEmail(evento.target.value)}
            required
            disabled={editando}
            maxLength={254}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo id="ct-cargo" rotulo="Cargo" opcional>
            <input
              id="ct-cargo"
              value={cargo}
              onChange={(evento) => setCargo(evento.target.value)}
              maxLength={120}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>

          <Campo id="ct-telefone" rotulo="Telefone" opcional>
            <input
              id="ct-telefone"
              value={telefone}
              onChange={(evento) => setTelefone(evento.target.value)}
              maxLength={40}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>
        </div>

        <Campo id="ct-status" rotulo="Situação">
          <select
            id="ct-status"
            value={status}
            onChange={(evento) => setStatus(evento.target.value as ContactStatus)}
            className={CLASSE_DE_CAMPO}
          >
            {opcoes.map((opcao) => (
              <option key={opcao.valor} value={opcao.valor}>
                {opcao.rotulo}
              </option>
            ))}
          </select>
        </Campo>

        <p
          className={
            derrubaSessoes
              ? 'rounded-md bg-warning/10 px-3 py-2 text-xs text-warning'
              : 'text-xs text-muted-foreground'
          }
        >
          {situacao.efeito}
          {derrubaSessoes
            ? ` Agora mesmo há ${contato.activeSessions} ${
                contato.activeSessions === 1 ? 'sessão aberta' : 'sessões abertas'
              }, e salvar derruba ${contato.activeSessions === 1 ? 'ela' : 'todas'}.`
            : ''}
        </p>

        {!editando && (
          <>
            <Campo id="ct-senha" rotulo="Senha inicial" opcional>
              <input
                id="ct-senha"
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                minLength={12}
                maxLength={1024}
                autoComplete="new-password"
                placeholder="Mínimo 12 caracteres"
                className={CLASSE_DE_CAMPO}
              />
            </Campo>
            {senha.trim().length > 0 && (
              <Campo id="ct-confirmar" rotulo="Confirmar senha">
                <input
                  id="ct-confirmar"
                  type="password"
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  className={CLASSE_DE_CAMPO}
                />
                {confirmarSenha && senha !== confirmarSenha && (
                  <p className="mt-1 text-xs text-destructive">As senhas não coincidem.</p>
                )}
              </Campo>
            )}
            {senha.trim().length > 0 && (
              <p className="text-xs text-muted-foreground">
                Com senha definida, o contato nasce ativo e já pode entrar no portal.
              </p>
            )}
          </>
        )}

        {escrita.erro ? (
          <p role="alert" className="text-sm text-destructive">
            {escrita.erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={escrita.mutacao.isPending}
            disabled={escrita.mutacao.isPending || (senha.trim().length > 0 && (senha.trim().length < 12 || senha !== confirmarSenha))}
          >
            {editando ? 'Salvar' : 'Cadastrar contato'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DefinirSenhaModal({
  accountId,
  contato,
  aberto,
  aoFechar,
}: {
  accountId: string;
  contato: AccountContact;
  aberto: boolean;
  aoFechar: () => void;
}) {
  const qc = useQueryClient();
  const [senha, setSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');

  const mut = useMutation({
    mutationFn: () => definirSenhaDeContato(accountId, contato.id, senha),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chavesDeClientes.contatos(accountId) });
      setSenha('');
      setConfirmar('');
      aoFechar();
    },
  });

  if (!aberto) return null;

  const invalido = senha.trim().length < 12 || senha !== confirmar;

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={`Definir senha — ${contato.name}`}
      descricao="A senha é do portal do cliente (suporte.aptum.com.br). O contato usa o e-mail e esta senha para entrar."
    >
      <form
        onSubmit={(e) => { e.preventDefault(); if (!invalido) mut.mutate(); }}
        className="space-y-4 px-5 py-4"
      >
        <Campo id="ds-senha" rotulo="Nova senha">
          <input
            id="ds-senha"
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoFocus
            minLength={12}
            maxLength={1024}
            autoComplete="new-password"
            placeholder="Mínimo 12 caracteres"
            className={CLASSE_DE_CAMPO}
          />
        </Campo>
        <Campo id="ds-confirmar" rotulo="Confirmar senha">
          <input
            id="ds-confirmar"
            type="password"
            value={confirmar}
            onChange={(e) => setConfirmar(e.target.value)}
            className={CLASSE_DE_CAMPO}
          />
          {confirmar && senha !== confirmar && (
            <p className="mt-1 text-xs text-destructive">As senhas não coincidem.</p>
          )}
        </Campo>
        {mut.error && (
          <p className="text-sm text-destructive">Erro ao definir a senha. Tente novamente.</p>
        )}
        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>Cancelar</Button>
          <Button type="submit" loading={mut.isPending} disabled={invalido}>
            Definir senha
          </Button>
        </div>
      </form>
    </Modal>
  );
}
